/**
 * Escritas da página de Contas pelos repositórios do app: transferência, ajuste de saldo (as três
 * naturezas do app/accounts/detail, ADR 0074), conta (criar, editar, arquivar) e caixinha.
 */
import { archiveAccount, createAccount, unarchiveAccount, updateAccount } from '@/db/repositories/accountRepository';
import { createReserva } from '@/db/repositories/reservaRepository';
import { createBalanceAdjustment, createTransaction, createTransfer, deleteTransaction } from '@/db/repositories/transactionRepository';
import type { AccountsEditor } from '../viewer/lib/editing';

export function createAccountsEditor(after: () => Promise<void>): AccountsEditor {
  const done = async <T,>(v: T): Promise<T> => { await after(); return v; };
  return {
    async transfer(input) {
      const t = await createTransfer({
        amount: input.amount, description: input.description?.trim() || 'Transferência', date: input.date,
        sourceAccountId: input.sourceAccountId, destinationAccountId: input.destinationAccountId, isConfirmed: input.isConfirmed,
      });
      return done(t.id);
    },
    async adjust(input) {
      const gap = input.difference;
      const reason = input.reason?.trim() || '';
      if (input.nature === 'neutral') {
        const t = await createBalanceAdjustment(input.accountId, input.newBalance, { date: input.date, description: reason || undefined });
        return done([t.id]);
      }
      if (input.nature === 'transfer') {
        if (!input.counterpartId) throw new Error('Escolha a outra conta.');
        const t = await createTransfer({
          amount: Math.abs(gap), description: reason || 'Ajuste de saldo', date: input.date,
          sourceAccountId: gap < 0 ? input.accountId : input.counterpartId,
          destinationAccountId: gap < 0 ? input.counterpartId : input.accountId,
        });
        return done([t.id]);
      }
      const t = await createTransaction({
        type: gap < 0 ? 'expense' : 'income', amount: Math.abs(gap),
        description: reason || (gap < 0 ? 'Despesa de ajuste' : 'Receita de ajuste'),
        date: input.date, accountId: input.accountId, isConfirmed: true,
      });
      return done([t.id]);
    },
    async createAccount(input) {
      const a = await createAccount({ name: input.name, type: input.type, initialBalance: input.initialBalance, isExcludedFromTotals: input.isExcludedFromTotals ? 1 : 0 } as Parameters<typeof createAccount>[0]);
      return done(a.id);
    },
    async updateAccount(id, input) {
      await updateAccount(id, { name: input.name, type: input.type, initialBalance: input.initialBalance, isExcludedFromTotals: input.isExcludedFromTotals ? 1 : 0 } as Parameters<typeof updateAccount>[1]);
      await after();
    },
    async archiveAccount(id) { await archiveAccount(id); await after(); },
    async unarchiveAccount(id) { await unarchiveAccount(id); await after(); },
    async createReserva(input) {
      const r = await createReserva({ accountId: input.accountId, name: input.name, targetAmount: input.targetAmount ?? null, deadline: input.deadline ?? null, initialAmount: input.initialAmount ?? 0 });
      return done(r.id);
    },
    async removeCreated(ids) {
      for (const id of ids) await deleteTransaction(id, 'single');
      await after();
    },
  };
}
